/**
 * Company settings: the seller block, the branding, the logo, and the jurisdiction (M7/M8).
 *
 * Read by anyone who may read an invoice -- the invoice screen shows who the invoice is
 * from -- and written by Super Admin alone. The screen enforces neither; it asks the
 * store what the user holds only to decide whether to render a form or a read-only card,
 * and the API refuses the write regardless.
 *
 * `tax_regime` is the switch (ADR-013). Nothing in this client hard-codes what a
 * jurisdiction calls things: the labels, the state list and the currency all arrive from
 * `/company-settings/regime`, because a form reading "GSTIN" over a box that now validates
 * an EIN is worse than either country on its own.
 */

import { API_PREFIX, apiFetch, apiGet } from '@/api/client'

export interface CompanySettings {
  id: string
  legal_name: string
  trade_name: string | null
  /** Which country's tax rules this deployment bills under. `RegimeInfo` names them. */
  tax_regime: string
  /** A GSTIN in India, an EIN or seller's permit in the US. One column, one form field. */
  tax_registration_id: string | null
  /** India only. Kept across a switch rather than cleared, so switching back loses nothing. */
  pan: string | null
  state_code: string | null
  state_name: string | null
  is_tax_registered: boolean

  address_line1: string | null
  address_line2: string | null
  city: string | null
  postal_code: string | null
  country: string
  email: string | null
  phone: string | null
  website: string | null

  currency: string
  invoice_terms: string | null
  invoice_footer: string | null
  bank_details: string | null

  commission_rate_pct: string
  commission_accrual: CommissionAccrual

  /**
   * Above this value a manual stock adjustment needs full inventory rights (M3).
   *
   * Not on the form, but carried through the round trip all the same: the PUT replaces the
   * whole record, so a field left out of the payload is a field reset to its default.
   */
  stock_adjustment_approval_value: string

  /**
   * The default delivery charge (M24-05): what an online or sales order pays when the
   * location it ships from has no rate of its own (`DeliveryRate`). Zero is free.
   */
  shipping_flat_rate: string
  /** Goods value above which the default charge is waived. `null` means it never is. */
  shipping_free_over: string | null

  logo_key: string | null
  /** Signed and short-lived. Never stored, never sent back -- the key is the durable thing. */
  logo_url: string | null
}

export const COMMISSION_ACCRUALS = ['issue', 'collection'] as const

export type CommissionAccrual = (typeof COMMISSION_ACCRUALS)[number]

export const ACCRUAL_LABELS: Record<CommissionAccrual, string> = {
  issue: 'When the invoice is issued',
  collection: 'When the money arrives',
}

/** Everything the PUT accepts. The whole record goes back, not a patch. */
export type CompanySettingsInput = Omit<
  CompanySettings,
  'id' | 'state_name' | 'is_tax_registered' | 'logo_key' | 'logo_url'
>

export interface StateOption {
  code: string
  name: string
}

/** One entry in the jurisdiction dropdown. */
export interface RegimeOption {
  code: string
  name: string
  country: string
  currency: string
}

/**
 * What the jurisdiction in force calls things, and what it offers to choose from.
 *
 * Fetched rather than hard-coded so that the day a client turns out to be American, the
 * labels, the state list and the currency change together with the rules behind them.
 */
export interface RegimeInfo {
  code: string
  name: string
  country: string
  currency: string
  locale: string
  /** "Tax Invoice" is a GST term with a statutory meaning; a US seller issues an "Invoice". */
  document_title: string
  registration_label: string
  customer_registration_label: string
  product_code_label: string
  place_label: string
  rounds_to_whole_currency: boolean
  /** `[primary, secondary, unified]` per supply type. A null slot is a column not shown. */
  component_labels: Record<string, (string | null)[]>
  /** Fixed rates a tax-rule form should offer as a dropdown (India's GST slabs), or `null`
   *  for a free-form percentage (the US, where a combined rate is whatever it adds up to). */
  rate_slabs: string[] | null
  states: StateOption[]
  available: RegimeOption[]
}

const BASE = `${API_PREFIX}/company-settings`

export const getCompanySettings = () => apiGet<CompanySettings>(BASE)

export const listStates = () => apiGet<StateOption[]>(`${BASE}/states`)

/**
 * The regime in force, or -- given a code -- the one a particular document was raised under.
 *
 * An invoice freezes its regime, so a client who switched jurisdiction in October must
 * still see September's invoices labelled the way they were printed.
 */
export const getRegime = (code?: string | null) =>
  apiGet<RegimeInfo>(code ? `${BASE}/regime?code=${encodeURIComponent(code)}` : `${BASE}/regime`)

export const updateCompanySettings = (input: CompanySettingsInput) =>
  apiGet<CompanySettings>(BASE, { method: 'PUT', body: JSON.stringify(input) })

/** Points the letterhead at a key that has already been uploaded. */
export const setCompanyLogo = (key: string) =>
  apiGet<CompanySettings>(`${BASE}/logo`, { method: 'POST', body: JSON.stringify({ key }) })

/** The two channels that deliver. POS never does: the customer carries the goods out. */
export const DELIVERY_CHANNELS = ['online', 'sales_order'] as const

export type DeliveryChannel = (typeof DELIVERY_CHANNELS)[number]

export const DELIVERY_CHANNEL_LABELS: Record<DeliveryChannel, string> = {
  online: 'Online shop',
  sales_order: 'Sales order',
}

/** A location's own delivery charge for one channel, overriding the company default. */
export interface DeliveryRate {
  id: string
  location_id: string
  channel: DeliveryChannel
  flat_rate: string
  /** Goods value from which the charge is waived. `null` means it never is. */
  free_over: string | null
}

export type DeliveryRateInput = Omit<DeliveryRate, 'id'>

/** The rate an order from a location would pay, and whether it is the location's own. */
export interface DeliveryQuote {
  flat_rate: string
  free_over: string | null
  source: 'location' | 'default'
}

export const listDeliveryRates = () => apiGet<DeliveryRate[]>(`${BASE}/delivery-rates`)

export const createDeliveryRate = (input: DeliveryRateInput) =>
  apiGet<DeliveryRate>(`${BASE}/delivery-rates`, { method: 'POST', body: JSON.stringify(input) })

export const updateDeliveryRate = (id: string, input: DeliveryRateInput) =>
  apiGet<DeliveryRate>(`${BASE}/delivery-rates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })

export const deleteDeliveryRate = (id: string) =>
  apiFetch<void>(`${BASE}/delivery-rates/${id}`, { method: 'DELETE' })

export const quoteDelivery = (channel: DeliveryChannel, locationId: string | null) =>
  apiGet<DeliveryQuote>(
    `${BASE}/delivery-rates/quote?channel=${channel}` +
      (locationId ? `&location_id=${encodeURIComponent(locationId)}` : ''),
  )

/**
 * What a quote charges on `goodsTotal`: nothing from the free-over threshold up, the flat
 * rate below it. The server applies the same rule when the order is saved.
 */
export function deliveryCharge(quote: DeliveryQuote, goodsTotal: number): number {
  if (quote.free_over !== null && goodsTotal >= Number(quote.free_over)) return 0
  return Number(quote.flat_rate)
}

/** The read shape minus the fields the server owns, ready to put in form state. */
export function toInput(settings: CompanySettings): CompanySettingsInput {
  return {
    legal_name: settings.legal_name,
    trade_name: settings.trade_name,
    tax_regime: settings.tax_regime,
    tax_registration_id: settings.tax_registration_id,
    pan: settings.pan,
    state_code: settings.state_code,
    address_line1: settings.address_line1,
    address_line2: settings.address_line2,
    city: settings.city,
    postal_code: settings.postal_code,
    country: settings.country,
    email: settings.email,
    phone: settings.phone,
    website: settings.website,
    currency: settings.currency,
    invoice_terms: settings.invoice_terms,
    invoice_footer: settings.invoice_footer,
    bank_details: settings.bank_details,
    commission_rate_pct: settings.commission_rate_pct,
    commission_accrual: settings.commission_accrual,
    stock_adjustment_approval_value: settings.stock_adjustment_approval_value,
    shipping_flat_rate: settings.shipping_flat_rate,
    shipping_free_over: settings.shipping_free_over,
  }
}

/** The one regime whose registration number carries a state code. See `stateOfRegistration`. */
export const INDIA_GST = 'india_gst'

/**
 * The state code a GSTIN carries, or `null` if it does not look like one yet.
 *
 * The form uses this to fill the state in as the user types rather than after the server
 * has refused a mismatch: the registration decides the state, and offering a dropdown
 * that can disagree with it invites the mistake that puts CGST on an inter-state supply.
 */
export function stateOfGstin(gstin: string | null | undefined): string | null {
  const value = (gstin ?? '').replace(/\s+/g, '').toUpperCase()
  return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(value) ? value.slice(0, 2) : null
}

/**
 * The state a registration proves, under the regime it was issued in -- `null` if none.
 *
 * India is the exception rather than the rule: a GSTIN is a state code followed by a PAN,
 * so the number settles the question and the dropdown follows it. Nothing in an EIN says
 * where the business is, so under any other regime the typed state stands on its own and
 * this returns `null`, which is the form's cue to leave the dropdown alone.
 */
export function stateOfRegistration(
  regime: string | null | undefined,
  registration: string | null | undefined,
): string | null {
  return regime === INDIA_GST ? stateOfGstin(registration) : null
}
